import {
  ClipboardCheck,
  ShieldPlus,
  FileText,
  Handshake,
  ShieldAlert,
  Flag,
} from "lucide-react";

/** DPDP Act compliance deadline — 13 May 2027 */
export const DPDP_COMPLIANCE_DEADLINE = new Date(2027, 4, 13);

export const ROAD_PHASES = [
  {
    id: "assess",
    label: "Gap Assessment",
    short: "Gap",
    description:
      "Baseline privacy maturity and identify gaps across departments.",
    icon: ClipboardCheck,
    route: "/user/gap-assessment",
    color: "#6366f1",
    weight: 1,
  },
  {
    id: "pia",
    label: "Privacy Impact Assessment",
    short: "PIA",
    description:
      "Evaluate high-risk processing activities and mitigation controls.",
    icon: ShieldPlus,
    route: "/user/pia",
    color: "#818cf8",
    weight: 1,
  },
  {
    id: "ropa",
    label: "Record of Processing Activities",
    short: "ROPA",
    description: "Document lawful purposes, data categories, and retention.",
    icon: FileText,
    route: "/user/ropa",
    color: "#2563eb",
    weight: 1,
  },
  {
    id: "tprm",
    label: "Third-Party Risk Management",
    short: "TPRM",
    description: "Assess vendors and processors handling personal data.",
    icon: Handshake,
    route: "/user/tprm",
    color: "#0ea5e9",
    weight: 1,
  },
  {
    id: "breach",
    label: "Breach Management",
    short: "Breach",
    description:
      "Incident response, notification workflows, and readiness drills.",
    icon: ShieldAlert,
    route: "/user/breach-management",
    color: "#dc2626",
    weight: 1,
  },
];

export const COMPLIANCE_GOAL = {
  id: "compliance",
  label: "DPDP Compliance",
  short: "Compliant",
  description:
    "Organisation-wide readiness aligned with the Digital Personal Data Protection Act, 2023.",
  icon: Flag,
  color: "#059669",
};

export function roadPhaseStatus(phaseId, progress) {
  const p = progress?.[phaseId];
  if (p == null) return "pending";
  if (p >= 100) return "complete";
  if (p > 0) return "in_progress";
  return "pending";
}

export function daysUntilDeadline(fromDate = new Date()) {
  const start = new Date(
    fromDate.getFullYear(),
    fromDate.getMonth(),
    fromDate.getDate(),
  );
  const end = new Date(
    DPDP_COMPLIANCE_DEADLINE.getFullYear(),
    DPDP_COMPLIANCE_DEADLINE.getMonth(),
    DPDP_COMPLIANCE_DEADLINE.getDate(),
  );
  return Math.max(0, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
}

export function formatDeadlineDate() {
  return DPDP_COMPLIANCE_DEADLINE.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** Start Date = organisation created date (tenant provisioning). */
export function timelineProgressPct(startDate, fromDate = new Date()) {
  if (!startDate) return 0;
  const start = new Date(startDate).getTime();
  const end = DPDP_COMPLIANCE_DEADLINE.getTime();
  const now = fromDate.getTime();
  if (!Number.isFinite(start) || end <= start) return 0;
  const pct = ((now - start) / (end - start)) * 100;
  return Math.min(100, Math.max(0, Math.round(pct)));
}

export function formatStartDate(startDate) {
  if (!startDate) return "—";
  const d = new Date(startDate);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Evenly spaced target dates from today through the DPDP deadline. */
export function phaseTargetDates(fromDate = new Date()) {
  const today = new Date(
    fromDate.getFullYear(),
    fromDate.getMonth(),
    fromDate.getDate(),
  );
  const deadline = DPDP_COMPLIANCE_DEADLINE;
  const totalMs = deadline - today;
  const n = ROAD_PHASES.length;

  return ROAD_PHASES.map((phase, i) => {
    const fraction = (i + 1) / (n + 1);
    const target = new Date(today.getTime() + totalMs * fraction);
    return { id: phase.id, targetDate: target };
  });
}

export function roadProgressScore(progress) {
  const vals = ROAD_PHASES.map((p) => Number(progress?.[p.id]) || 0);
  if (!vals.length) return 0;
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
}

export function completedRoadPhases(progress) {
  return ROAD_PHASES.filter(
    (p) => roadPhaseStatus(p.id, progress) === "complete",
  ).length;
}

function formatShortDate(date) {
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function urgencyForPhase(phase, progress, targetDate, daysLeft) {
  const status = roadPhaseStatus(phase.id, progress);
  if (status === "complete") return "done";
  const overdue = new Date() > targetDate;
  if (overdue || daysLeft <= 90) return "critical";
  if (daysLeft <= 180 || status === "in_progress") return "high";
  if (status === "pending") return "medium";
  return "low";
}

/**
 * Smart, timeline-aware recommendations to reach DPDP compliance by 13 May 2027.
 */
export function buildComplianceRecommendations({
  progress = {},
  orgName = "your organisation",
}) {
  const now = new Date();
  const daysLeft = daysUntilDeadline(now);
  const targets = Object.fromEntries(
    phaseTargetDates(now).map((t) => [t.id, t.targetDate]),
  );
  const roadScore = roadProgressScore(progress);
  const done = completedRoadPhases(progress);
  const total = ROAD_PHASES.length;
  const nextPhase = ROAD_PHASES.find(
    (p) => roadPhaseStatus(p.id, progress) !== "complete",
  );
  const recs = [];

  recs.push({
    id: "deadline-countdown",
    priority:
      daysLeft <= 120 ? "critical" : daysLeft <= 240 ? "high" : "medium",
    title: `${daysLeft} days until the DPDP compliance deadline`,
    body: `The Digital Personal Data Protection Act obligations apply from ${formatDeadlineDate()}. ${orgName} is ${roadScore}% ready across the five core modules.`,
    action: nextPhase
      ? `Focus on ${nextPhase.label}`
      : "Maintain ongoing compliance",
    route: nextPhase?.route || "/user/dashboard",
    icon: "calendar",
  });

  if (nextPhase) {
    const target = targets[nextPhase.id];
    const status = roadPhaseStatus(nextPhase.id, progress);
    const urgency = urgencyForPhase(nextPhase, progress, target, daysLeft);
    const pct = Math.round(progress?.[nextPhase.id] ?? 0);

    recs.push({
      id: `next-${nextPhase.id}`,
      priority: urgency === "done" ? "low" : urgency,
      title:
        status === "in_progress"
          ? `Continue ${nextPhase.label} (${pct}% complete)`
          : `Start ${nextPhase.label} — Phase ${ROAD_PHASES.indexOf(nextPhase) + 1} of ${total}`,
      body:
        status === "in_progress"
          ? `${nextPhase.description} Target completion: ${formatShortDate(target)} to stay on the road to compliance.`
          : `${nextPhase.description} Aim to finish by ${formatShortDate(target)} (${Math.ceil((target - now) / (1000 * 60 * 60 * 24))} days from now).`,
      action: status === "in_progress" ? "Resume module" : "Open module",
      route: nextPhase.route,
      icon: "route",
    });
  }

  ROAD_PHASES.forEach((phase) => {
    const status = roadPhaseStatus(phase.id, progress);
    const target = targets[phase.id];
    if (status === "complete") return;
    if (now <= target) return;

    recs.push({
      id: `overdue-${phase.id}`,
      priority: "critical",
      title: `${phase.label} is behind schedule`,
      body: `The recommended target was ${formatShortDate(target)}. Prioritise this module to recover time before ${formatShortDate(DPDP_COMPLIANCE_DEADLINE)}.`,
      action: `Complete ${phase.short}`,
      route: phase.route,
      icon: "alert",
    });
  });

  if (
    roadPhaseStatus("assess", progress) === "complete" &&
    roadPhaseStatus("ropa", progress) !== "complete"
  ) {
    recs.push({
      id: "ropa-after-assess",
      priority: "medium",
      title: "Map processing activities in ROPA",
      body: "Use gap assessment findings to populate your Record of Processing Activities — required for lawful processing under DPDP Section 8.",
      action: "Open ROPA",
      route: "/user/ropa",
      icon: "document",
    });
  }

  if (roadPhaseStatus("tprm", progress) !== "complete") {
    recs.push({
      id: "vendor-dpa",
      priority:
        roadPhaseStatus("tprm", progress) === "in_progress" ? "high" : "medium",
      title: "Review vendor data processing agreements",
      body: "Execute DPAs with all processors and document sub-processors before the compliance deadline — a key TPRM milestone under DPDP Section 17.",
      action: "Assess vendors",
      route: "/user/tprm",
      icon: "handshake",
    });
  }

  if (roadPhaseStatus("breach", progress) !== "complete") {
    recs.push({
      id: "breach-readiness",
      priority: daysLeft <= 180 ? "high" : "medium",
      title: "Establish breach notification readiness",
      body: "Define 72-hour incident response, Board notification workflows, and data principal communication templates per DPDP Section 8(6).",
      action: "Set up breach management",
      route: "/user/breach-management",
      icon: "shield",
    });
  }

  if (done === total) {
    recs.push({
      id: "all-complete",
      priority: "medium",
      title: "All core modules complete — sustain compliance",
      body: `Schedule quarterly reviews, policy refreshes, and consent/DSAR monitoring through ${formatDeadlineDate()} and beyond.`,
      action: "View dashboard",
      route: "/user/dashboard",
      icon: "check",
    });
  } else if (daysLeft <= 365 && done < 3) {
    recs.push({
      id: "accelerate",
      priority: "critical",
      title: "Accelerate your compliance programme",
      body: `With ${daysLeft} days remaining and ${total - done} modules incomplete, assign module owners and run parallel workstreams where possible.`,
      action: "View gap assessment",
      route: "/user/gap-assessment",
      icon: "zap",
    });
  }

  const order = { critical: 0, high: 1, medium: 2, low: 3, done: 4 };
  const seen = new Set();
  return recs
    .filter((r) => {
      if (seen.has(r.id)) return false;
      seen.add(r.id);
      return true;
    })
    .sort((a, b) => order[a.priority] - order[b.priority])
    .slice(0, 6);
}
